import { Box, Tooltip, Typography } from '@mui/material';
import { HMIField, HMILiveValues } from '../../types';

export interface ImageOverlayFieldProps {
  field: HMIField;
  /** Every field in the template, not just this section - overlay pins can reference a field defined anywhere. */
  allFields: HMIField[];
  /** Full live-values map (not scoped to this section), keyed by field id. */
  values: HMILiveValues;
}

/** Same formatting rules every other field kind uses (enum label, bool on/off, decimals, unit). */
function formatValue(field: HMIField | undefined, raw: unknown): string {
  if (raw === undefined || raw === null || raw === '') return '--';
  if (typeof raw === 'boolean') return raw ? 'On' : 'Off';
  if (field?.datatype === 'enum' && field.options) {
    const opt = field.options.find((o) => o.value === raw);
    if (opt) return opt.label;
  }
  const num = typeof raw === 'number' ? raw : Number(raw);
  const display =
    typeof raw === 'number' && field?.decimals !== undefined && !Number.isNaN(num)
      ? num.toFixed(field.decimals)
      : String(raw);
  return field?.unit ? `${display} ${field.unit}` : display;
}

/**
 * Renders a `kind: "image"` field: a picture with small live-value badges
 * pinned at builder-authored spots (see `HMIImageOverlayItem`), mirroring
 * the Digital Twin > Live Monitor "image property mapper" idea, but reading
 * from the same live-values store every other HMI field uses instead of a
 * separate SignalR/backend pipeline - so pins update automatically whenever
 * the field they reference changes, with zero extra wiring.
 */
function ImageOverlayField({ field, allFields, values }: ImageOverlayFieldProps) {
  const items = field.overlayItems ?? [];

  if (!field.imageData) {
    return (
      <Box
        sx={{
          p: 2,
          textAlign: 'center',
          color: 'text.secondary',
          border: '1px dashed',
          borderColor: 'divider',
          borderRadius: 1,
        }}
      >
        <Typography variant="body2">No image configured for this widget yet.</Typography>
      </Box>
    );
  }

  return (
    <Box sx={{ position: 'relative', width: '100%', lineHeight: 0 }}>
      <Box
        component="img"
        src={field.imageData}
        alt={field.caption}
        sx={{ width: '100%', display: 'block', borderRadius: 1 }}
      />
      {items.map((item) => {
        const source = allFields.find((f) => f.id === item.fieldId);
        const label = `${source?.caption ?? item.fieldId}: ${formatValue(source, values[item.fieldId])}`;
        return (
          <Tooltip key={item.fieldId} title={source?.caption ?? item.fieldId}>
            <Box
              sx={{
                position: 'absolute',
                left: `${item.x}%`,
                top: `${item.y}%`,
                transform: 'translate(-50%, -50%)',
                bgcolor: 'primary.main',
                color: 'primary.contrastText',
                px: 1,
                py: 0.25,
                borderRadius: 4,
                fontSize: 12,
                fontWeight: 500,
                whiteSpace: 'nowrap',
                boxShadow: 1,
              }}
            >
              {label}
            </Box>
          </Tooltip>
        );
      })}
    </Box>
  );
}

export default ImageOverlayField;
